import { useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { toast } from 'sonner';
import type { WorkBook } from 'xlsx';
import { UploadSimple, X, FileXls, DownloadSimple } from '@phosphor-icons/react';
import type { ExpectedField, ImportTarget } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/utils';
import { downloadBlob } from '../../../lib/browserDownload';
import { SheetRulesPopover } from './SheetRulesPopover';
import { WizardFooter } from './ImportWizardFooterSlot';
import { MAX_ROWS_BY_TARGET, MAX_SHEET_BYTES, MAX_SHEET_ROWS, MAX_SHEET_SIZE_LABEL, formatLimit } from './importLimits';
import {
  compactFieldKey,
  countLabel,
  estimatePreviewColumnWidths,
  ignoreSurfacedError,
  pluralWord,
} from './importWizardHelpers';
import { TruncatedText } from '../../../components/common/TruncatedText';
import { knownHeaderNames } from './sheetHeaderAliases';
import {
  chooseDataSheet,
  csvRows,
  decodeCsvText,
  isCsvFile,
  splitAtHeader,
  WORKBOOK_READ_OPTIONS,
  worksheetRows,
  type PreviewRows,
  type PreviewSheet,
} from './sheetPreviewRows';

export const ACCEPTED_SHEET_EXTENSIONS = ['.xlsx', '.csv'] as const;

const FILE_INPUT_ID = 'import-wizard-file-input';
const FILE_INPUT_ACCEPT = ACCEPTED_SHEET_EXTENSIONS.join(',');
const WRONG_TYPE_MESSAGE = "This file type isn't supported. Upload an .xlsx or .csv file.";
const TOO_BIG_MESSAGE = `This file is larger than ${MAX_SHEET_SIZE_LABEL}.`;
const PASTE_INPUT_ID = 'import-wizard-paste-handles';
const PASTE_PLACEHOLDER = '@creator_one\ncreator_two\nhttps://instagram.com/creator_three';
const PASTE_ROWS = 4;
const PASTE_HINT = 'One handle or profile link per line. Or upload a sheet below.';
/** A paste is uploaded as a one-column sheet under the handle field's key, so
 *  the server maps it without asking. */
const PASTED_SHEET_HEADER = 'username';
export const PASTED_SHEET_NAME = 'pasted-handles.csv';
const CSV_MIME_TYPE = 'text/csv';
// Newlines and commas, plus the tabs and semicolons a row copied from a
// spreadsheet arrives with.
const PASTE_SEPARATORS = /[\r\n,;\t]+/;
const ONE_FILE_MESSAGE = 'Upload one file at a time.';
const MAX_FILES_PER_UPLOAD = 1;
const TEMPLATE_FILE_NAME = 'creator-sheet-template.xlsx';
const TEMPLATE_SHEET_NAME = 'Sheet1';
const TEMPLATE_FAILED_MESSAGE = "Couldn't generate the template — try again.";
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PREVIEW_DATA_ROWS = 5;
const PREVIEW_COLUMNS = 7;

/** Shared by the drop zone and the file card that replaces it, so the two
 *  can't drift apart. */
const DASHED_SURFACE_CLASS = 'rounded-xl border-[1.5px] border-dashed';
const DASHED_SURFACE_IDLE_CLASS = 'border-border bg-muted/50';
const DASHED_SURFACE_ACTIVE_CLASS = 'border-foreground bg-muted';

const PREVIEW_HEADER_CELL_CLASS = 'border-b border-border px-3 py-3 text-body-sm-regular text-muted-foreground';
const PREVIEW_CELL_CLASS = 'px-3 py-3 text-foreground';

/** A sheet already on the server (Back from mapping): shown as a file card
 *  instead of the drop zone, with Continue instead of Upload. */
export interface UploadedSheet {
  fileName: string;
  /** Null when the server didn't say (a resumed batch from an older server). */
  rowCount: number | null;
  onContinue: () => void;
  /** Discards the uploaded sheet so a different file can be picked. */
  onRemove: () => Promise<void>;
}

interface ImportUploadStepProps {
  /** Default 'pipeline'. Outreach adds a paste box and its own row limit. */
  target?: ImportTarget;
  payoutModel?: string;
  campaignPlatform?: string;
  /** This campaign's importable columns — the header row of the downloadable
   *  template, and what Sheet rules describes. Empty while checkStatus (fired
   *  when the wizard opens) is still in flight. */
  expectedFields: ExpectedField[];
  busy: boolean;
  onUpload: (file: File) => Promise<void>;
  uploaded?: UploadedSheet | null;
}

interface SheetPreview {
  /** Header row plus up to PREVIEW_DATA_ROWS data rows, each cut to PREVIEW_COLUMNS cells. */
  rows: string[][];
  dataRowCount: number;
  /** Data rows beyond the ones shown (excludes the header row). */
  moreRows: number;
}

type PreviewState = SheetPreview | 'loading' | 'error' | null;

function hasAcceptedExtension(fileName: string): boolean {
  const lowerName = fileName.toLowerCase();
  return ACCEPTED_SHEET_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
}

/** The reason a picked file can't be uploaded, or null when it can. Checked
 *  client-side before any request so an admin gets an instant answer. */
export function sheetFileError(file: { name: string; size: number }): string | null {
  if (!hasAcceptedExtension(file.name)) return WRONG_TYPE_MESSAGE;
  if (file.size > MAX_SHEET_BYTES) return TOO_BIG_MESSAGE;
  return null;
}

/** Builds a blank .xlsx with just the header row (one column per expected
 *  field, machine-readable key — not the display label) and triggers a
 *  browser download. Loads the xlsx lib lazily, same as the preview below. */
async function downloadTemplate(expectedFields: ExpectedField[]): Promise<void> {
  const XLSX = await import('xlsx');
  const sheet = XLSX.utils.aoa_to_sheet([expectedFields.map(compactFieldKey)]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, TEMPLATE_SHEET_NAME);
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  downloadBlob(new Blob([buffer], { type: XLSX_MIME_TYPE }), TEMPLATE_FILE_NAME);
}

type XlsxModule = typeof import('xlsx');

/** The rows of each sheet as the server reads them (stored values, hidden
 *  columns left out, hidden rows flagged; see worksheetRows). Sheets are read one at a
 *  time, as the first one with data is all that is needed. */
function* workbookSheets(XLSX: XlsxModule, workbook: WorkBook): Generator<PreviewRows> {
  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    yield worksheetRows(sheet, XLSX);
  }
}

async function readSheetRows(file: File): Promise<PreviewRows | null> {
  const buffer = await file.arrayBuffer();
  if (isCsvFile(file)) return csvRows(decodeCsvText(buffer));
  const XLSX = await import('xlsx');
  return chooseDataSheet(workbookSheets(XLSX, XLSX.read(buffer, WORKBOOK_READ_OPTIONS)));
}

function previewOf(sheet: PreviewSheet | null): SheetPreview {
  if (!sheet) return { rows: [], dataRowCount: 0, moreRows: 0 };
  const rows = [sheet.header, ...sheet.dataRows.slice(0, PREVIEW_DATA_ROWS)].map((row) => row.slice(0, PREVIEW_COLUMNS));
  const dataRowCount = sheet.dataRows.length;
  return { rows, dataRowCount, moreRows: Math.max(0, dataRowCount - PREVIEW_DATA_ROWS) };
}

interface PreviewContext {
  expectedFields: ExpectedField[];
  campaignPlatform?: string;
}

/** Header + first data rows of the sheet the server will import, as strings,
 *  for a quick client-side glance before the real server-side parse: the first
 *  sheet with data, under the header row the server finds in it (a title or
 *  notes above it are skipped), counting only non-blank rows. A .csv is read
 *  here, not by SheetJS, so its delimiter and byte-order mark are handled as
 *  the server does. The rows are read once (xlsx loads lazily — most sessions
 *  never open this wizard); finding the header is redone when the campaign's
 *  field list arrives, as its column names decide which row is the header. */
function previewFromRows(rows: PreviewRows | null, { expectedFields, campaignPlatform }: PreviewContext): SheetPreview {
  if (!rows) return previewOf(null);
  return previewOf(splitAtHeader(rows, knownHeaderNames(expectedFields, campaignPlatform)));
}

function isLoadedPreview(preview: PreviewState): preview is SheetPreview {
  return preview !== null && preview !== 'loading' && preview !== 'error' && preview.rows.length > 0;
}

function uploadHint(maxRows: number): string {
  return `.xlsx or .csv · up to ${formatLimit(maxRows)} creators · ${MAX_SHEET_SIZE_LABEL}`;
}

/** The server's row limit, answered from the client-side preview so an
 *  oversized sheet is not uploaded only to be refused. The preview counts the
 *  non-blank rows under the header, as the server does, but cannot see a sheet
 *  the server refuses for another reason: null never means it will accept it. */
export function sheetRowLimitError(dataRowCount: number | null, maxRows: number = MAX_SHEET_ROWS): string | null {
  if (dataRowCount === null || dataRowCount <= maxRows) return null;
  return `This sheet has more than ${formatLimit(maxRows)} rows. Split it into smaller files.`;
}

/** The handles in a paste: one per line (commas, semicolons and tabs also split), blanks dropped. */
function pastedHandles(text: string): string[] {
  return text
    .split(PASTE_SEPARATORS)
    .map((handle) => handle.trim())
    .filter((handle) => handle !== '');
}

function csvCell(value: string): string {
  return `"${value.replaceAll('"', '""')}"`;
}

/** A paste as the one-column sheet the import takes. */
function pastedHandlesSheet(handles: string[]): File {
  const csv = [PASTED_SHEET_HEADER, ...handles.map(csvCell)].join('\n');
  return new File([csv], PASTED_SHEET_NAME, { type: CSV_MIME_TYPE });
}

interface FileCardProps {
  fileName: string;
  rowCount: number | null;
  disabled?: boolean;
  onRemove: () => void;
}

function FileCard({ fileName, rowCount, disabled = false, onRemove }: FileCardProps) {
  return (
    <div className={cn('flex items-center gap-3 px-4 py-3', DASHED_SURFACE_CLASS, DASHED_SURFACE_IDLE_CLASS)}>
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-card">
        <FileXls size={16} className="text-foreground" />
      </div>
      <TruncatedText
        text={fileName}
        className="min-w-0 flex-1 text-left text-body-sm-medium text-foreground"
      />
      {rowCount !== null && (
        <span className="shrink-0 text-caption-regular text-muted-foreground">{countLabel(rowCount, 'row', 'rows')}</span>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Remove file"
        disabled={disabled}
        onClick={onRemove}
        className="-mr-1 text-muted-foreground hover:text-foreground"
      >
        {/* The class, not the size prop: Button resizes any icon without a size class. */}
        <X className="size-3.5" />
      </Button>
    </div>
  );
}

interface DropZoneProps {
  hint: string;
  dragActive: boolean;
  onOpenPicker: () => void;
  onDragActiveChange: (active: boolean) => void;
  onDropFiles: (files: File[]) => void;
}

function DropZone({ hint, dragActive, onOpenPicker, onDragActiveChange, onDropFiles }: DropZoneProps) {
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    onDragActiveChange(true);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    onDragActiveChange(false);
    onDropFiles(Array.from(e.dataTransfer.files));
  };

  // Not role="button"/tabIndex — it contains a real interactive element (the
  // "Choose file" button) and an interactive ARIA role wrapping another is an
  // axe nested-interactive violation. Keyboard users lose nothing: that
  // button is independently focusable and does the same thing. The onClick
  // here is only a mouse convenience for clicking anywhere in the zone.
  return (
    <div
      onClick={onOpenPicker}
      onDragOver={handleDragOver}
      onDragLeave={() => onDragActiveChange(false)}
      onDrop={handleDrop}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-3 px-6 py-8 text-center transition-colors',
        DASHED_SURFACE_CLASS,
        dragActive ? DASHED_SURFACE_ACTIVE_CLASS : `${DASHED_SURFACE_IDLE_CLASS} hover:bg-muted`,
      )}
    >
      <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-card">
        <UploadSimple size={20} className="text-foreground" />
      </div>
      <p className="text-body-sm-medium text-foreground">Drag and drop your file here</p>
      <p className="text-caption-regular text-muted-foreground">{hint}</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={(e) => { e.stopPropagation(); onOpenPicker(); }}
      >
        Choose file
      </Button>
    </div>
  );
}

function PreviewTable({ preview }: { preview: SheetPreview }) {
  const [headerRow = [], ...dataRows] = preview.rows;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full border-collapse text-left text-body-sm-regular" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {estimatePreviewColumnWidths(preview.rows).map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {/* The sheet's own header text: a shortened spelling here would
                  show a column name that is not in the admin's file. */}
              {headerRow.map((header, i) => (
                <th key={i} className={PREVIEW_HEADER_CELL_CLASS}>
                  <TruncatedText text={header} placeholder="—" className="block" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, i) => (
              <tr key={i} className="border-b border-border last:border-b-0">
                {row.map((cell, j) => (
                  <td key={j} className={PREVIEW_CELL_CLASS}>
                    <TruncatedText text={cell || ''} content={cell} placeholder="—" className="block" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.moreRows > 0 && (
        <p className="text-caption-regular text-muted-foreground">+{preview.moreRows} more {pluralWord(preview.moreRows, 'row', 'rows')}</p>
      )}
    </div>
  );
}

function PreviewSection({ preview }: { preview: PreviewState }) {
  if (preview === 'loading') {
    return <p className="text-caption-regular text-muted-foreground">Reading preview…</p>;
  }
  if (preview === 'error') {
    return (
      <p className="text-caption-regular text-muted-foreground">
        Couldn&apos;t preview this file — you can still upload it.
      </p>
    );
  }
  if (!isLoadedPreview(preview)) return null;
  return <PreviewTable preview={preview} />;
}

interface UploadIntroProps {
  target: ImportTarget;
  payoutModel?: string;
  campaignPlatform?: string;
  expectedFields: ExpectedField[];
}

// Only the Sheet rules link: the column names an admin can use are in the
// rules and in the downloadable template, not listed above the drop zone.
function UploadIntro({ target, payoutModel, campaignPlatform, expectedFields }: UploadIntroProps) {
  return (
    <div className="flex justify-end">
      <SheetRulesPopover target={target} payoutModel={payoutModel} campaignPlatform={campaignPlatform} expectedFields={expectedFields} />
    </div>
  );
}

function TemplateButton({ expectedFields }: { expectedFields: ExpectedField[] }) {
  const requestTemplate = () => {
    downloadTemplate(expectedFields).catch(() => toast.error(TEMPLATE_FAILED_MESSAGE));
  };
  return (
    <button
      type="button"
      disabled={expectedFields.length === 0}
      onClick={requestTemplate}
      className="inline-flex items-center gap-1.5 text-body-sm-medium text-foreground disabled:cursor-not-allowed disabled:text-muted-foreground"
    >
      <DownloadSimple size={14} /> Download template
    </button>
  );
}

interface UploadedSheetStepProps {
  uploaded: UploadedSheet;
  busy: boolean;
  expectedFields: ExpectedField[];
}

function UploadedSheetStep({ uploaded, busy, expectedFields }: UploadedSheetStepProps) {
  const remove = () => {
    uploaded.onRemove().catch(ignoreSurfacedError);
  };
  return (
    <div className="flex flex-col gap-4">
      <FileCard fileName={uploaded.fileName} rowCount={uploaded.rowCount} disabled={busy} onRemove={remove} />
      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <TemplateButton expectedFields={expectedFields} />
          <Button type="button" variant="default" size="lg" disabled={busy} onClick={uploaded.onContinue}>
            Continue
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}

export function ImportUploadStep({ uploaded, ...props }: ImportUploadStepProps) {
  if (!uploaded) return <PickSheetStep {...props} />;
  return <UploadedSheetStep uploaded={uploaded} busy={props.busy} expectedFields={props.expectedFields} />;
}

/** What reading the picked file has produced so far. */
type SheetRead = { status: 'idle' | 'loading' | 'error' } | { status: 'read'; rows: PreviewRows | null };

const IDLE_READ: SheetRead = { status: 'idle' };

/** The picked file's preview. Reading it is async, so each read is numbered
 *  and only the latest one may land — otherwise a slow read of a file the
 *  admin has since removed or replaced would show under the wrong file. */
function useSheetPreview(context: PreviewContext) {
  const [sheetRead, setSheetRead] = useState<SheetRead>(IDLE_READ);
  const latestRequestRef = useRef(0);
  const { expectedFields, campaignPlatform } = context;

  const clear = () => {
    latestRequestRef.current += 1;
    setSheetRead(IDLE_READ);
  };

  const read = (file: File) => {
    latestRequestRef.current += 1;
    const request = latestRequestRef.current;
    const applyIfLatest = (next: SheetRead) => {
      if (latestRequestRef.current === request) setSheetRead(next);
    };
    setSheetRead({ status: 'loading' });
    readSheetRows(file)
      .then((rows) => applyIfLatest({ status: 'read', rows }))
      .catch(() => applyIfLatest({ status: 'error' }));
  };

  const preview = useMemo<PreviewState>(() => {
    if (sheetRead.status === 'idle') return null;
    if (sheetRead.status !== 'read') return sheetRead.status;
    return previewFromRows(sheetRead.rows, { expectedFields, campaignPlatform });
  }, [sheetRead, expectedFields, campaignPlatform]);

  return { preview, read, clear };
}

interface PasteHandlesProps {
  value: string;
  onChange: (value: string) => void;
}

function PasteHandles({ value, onChange }: PasteHandlesProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={PASTE_INPUT_ID}>Paste handles</Label>
      <Textarea
        id={PASTE_INPUT_ID}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={PASTE_PLACEHOLDER}
        rows={PASTE_ROWS}
      />
      <p className="text-caption-regular text-muted-foreground">{PASTE_HINT}</p>
    </div>
  );
}

/** What Upload sends: the picked file, else the paste as a sheet, else nothing. */
function uploadSource(file: File | null, handles: string[]): File | null {
  if (file) return file;
  return handles.length > 0 ? pastedHandlesSheet(handles) : null;
}

function pasteRowLimitError(handles: string[], maxRows: number): string | null {
  return sheetRowLimitError(handles.length, maxRows);
}

function PickSheetStep({ target = 'pipeline', payoutModel, campaignPlatform, expectedFields, busy, onUpload }: ImportUploadStepProps) {
  const allowPaste = target === 'outreach';
  const maxRows = MAX_ROWS_BY_TARGET[target];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [pasted, setPasted] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { preview, read: readPreview, clear: clearPreview } = useSheetPreview({ expectedFields, campaignPlatform });

  const resetFileInput = () => {
    // Without this, re-picking the same file after removing or rejecting it
    // fires no change event.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearFile = () => {
    setFile(null);
    clearPreview();
    setFileError(null);
    resetFileInput();
  };

  const rejectFile = (message: string) => {
    setFile(null);
    clearPreview();
    setFileError(message);
    resetFileInput();
  };

  const acceptFile = (next: File) => {
    setFile(next);
    setFileError(null);
    readPreview(next);
  };

  const selectFile = (next: File | undefined) => {
    if (!next) return;
    const error = sheetFileError(next);
    if (error) {
      rejectFile(error);
      return;
    }
    acceptFile(next);
  };

  // The picker takes one file; a drop can carry several, and silently taking
  // the first would upload a file the admin didn't single out.
  const selectDroppedFiles = (files: File[]) => {
    if (files.length > MAX_FILES_PER_UPLOAD) {
      rejectFile(ONE_FILE_MESSAGE);
      return;
    }
    selectFile(files[0]);
  };

  const openPicker = () => fileInputRef.current?.click();
  const handles = allowPaste && !file ? pastedHandles(pasted) : [];
  const source = uploadSource(file, handles);
  const uploadPicked = () => {
    if (source) onUpload(source).catch(ignoreSurfacedError);
  };
  const previewRowCount = isLoadedPreview(preview) ? preview.dataRowCount : null;
  const shownError = fileError ?? sheetRowLimitError(previewRowCount, maxRows) ?? pasteRowLimitError(handles, maxRows);

  return (
    <div className="flex flex-col gap-4">
      {file ? (
        <FileCard fileName={file.name} rowCount={previewRowCount} onRemove={clearFile} />
      ) : (
        <>
          <UploadIntro target={target} payoutModel={payoutModel} campaignPlatform={campaignPlatform} expectedFields={expectedFields} />
          {allowPaste && <PasteHandles value={pasted} onChange={setPasted} />}
          <DropZone
            hint={uploadHint(maxRows)}
            dragActive={dragActive}
            onOpenPicker={openPicker}
            onDragActiveChange={setDragActive}
            onDropFiles={selectDroppedFiles}
          />
        </>
      )}

      <label className="sr-only" htmlFor={FILE_INPUT_ID}>Choose file</label>
      <input
        ref={fileInputRef}
        id={FILE_INPUT_ID}
        type="file"
        accept={FILE_INPUT_ACCEPT}
        onChange={(e) => selectFile(e.target.files?.[0])}
        className="hidden"
      />

      {shownError && (
        <p role="alert" className="text-body-sm-regular text-destructive">{shownError}</p>
      )}

      <PreviewSection preview={preview} />

      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <TemplateButton expectedFields={expectedFields} />
          <Button
            type="button"
            variant="default"
            size="lg"
            loading={busy}
            disabled={source === null || shownError !== null}
            onClick={uploadPicked}
          >
            {!busy && <UploadSimple size={14} />}
            {busy ? 'Uploading…' : 'Upload sheet'}
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
